import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../hooks/useAppState';
import type { GenerationOptions, FileUpload, Flashcard } from '../types';
import { generateFlashcards } from '../services/ai';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { TextArea } from '../components/ui/TextArea';
import { Select } from '../components/ui/Select';
import { Switch } from '../components/ui/Switch';
import { FileUploader } from '../components/FileUploader';
import { FlashcardCard } from '../components/FlashcardCard';
import { combineFileContents } from '../services/fileProcessor';
import {
  Wand2, Settings, ArrowLeft, ArrowRight, CheckCircle, Loader2, Sparkles,
  AlertCircle, Plus, Cpu, RefreshCw, Zap,
} from 'lucide-react';
import type { GenerationMetadata } from '../services/ai';

export const GeneratePage: React.FC = () => {
  const { addDeck, settings } = useAppState();
  const navigate = useNavigate();

  const [step, setStep] = useState<'upload' | 'options' | 'generating' | 'review'>('upload');
  const [uploadedFiles, setUploadedFiles] = useState<FileUpload[]>([]);
  const [textInput, setTextInput] = useState('');
  const [generatedCards, setGeneratedCards] = useState<Flashcard[]>([]);
  const [generationMeta, setGenerationMeta] = useState<GenerationMetadata | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAddingMore, setIsAddingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState('');
  const [additionalCount, setAdditionalCount] = useState(5);

  const configuredProviders = settings.aiProviders || [];
  const hasConfiguredProviders = configuredProviders.length > 0;
  const hasValidProvider = configuredProviders.some(p => p.selectedModel && (p.id === 'pollination' || p.apiKey || p.serverKeyAvailable));

  const getEffectiveProvider = () => {
    if (configuredProviders.length > 0 && selectedProviderId && selectedModel) {
      const p = configuredProviders.find(pp => pp.id === selectedProviderId);
      if (p?.selectedModel) return p;
    }
    return configuredProviders[0];
  };

  const availableProviders = configuredProviders.filter(p => p.selectedModel && (p.id === 'pollination' || p.apiKey || p.serverKeyAvailable));

  const defaultProvider = availableProviders[0];
  const defaultModel = defaultProvider?.selectedModel || 'openai';

  const [selectedProviderId, setSelectedProviderId] = useState<string>(defaultProvider?.id || '');
  const [selectedModel, setSelectedModel] = useState<string>(defaultModel);

  const [options, setOptions] = useState<GenerationOptions>({
    cardCount: 10,
    difficulty: 'medium',
    includeHints: true,
    includeImages: false,
    language: settings.language || 'en',
    provider: defaultProvider?.id || 'pollination',
    model: defaultModel,
    deckName: '',
    tags: [],
    outputFormat: 'basic',
  });

  const providerOptions = availableProviders.map(p => ({
    value: p.id,
    label: `${p.name}${!p.apiKey && p.serverKeyAvailable ? ' (Server key)' : ''}`,
  }));

  const getModelOptions = () => {
    const provider = configuredProviders.find(p => p.id === selectedProviderId);
    if (!provider?.models?.length) {
      if (provider?.id === 'pollination') {
        return [
          { value: 'openai', label: 'OpenAI' },
          { value: 'claude', label: 'Claude' },
          { value: 'gemini', label: 'Gemini' },
        ];
      }
      return [];
    }
    return provider.models.map((m: string) => ({ value: m, label: m }));
  };

  const handleProviderChange = (providerId: string) => {
    setSelectedProviderId(providerId);
    const provider = configuredProviders.find(p => p.id === providerId);
    if (provider?.selectedModel) {
      setSelectedModel(provider.selectedModel);
      setOptions(prev => ({ ...prev, provider: providerId, model: provider.selectedModel }));
    }
  };

  const handleModelChange = (model: string) => {
    setSelectedModel(model);
    setOptions(prev => ({ ...prev, model }));
  };

  const handleFilesProcessed = (files: FileUpload[]) => {
    setUploadedFiles(files);
    if (files.some(f => f.processed)) setStep('options');
  };

  const getSourceText = () => {
    const fileText = combineFileContents(uploadedFiles);
    const text = textInput.trim();
    if (fileText && text) return `${fileText}\n\n${text}`;
    return fileText || text;
  };

  const sourceText = getSourceText();

  const handleGenerate = async (addMore: boolean = false) => {
    if (!sourceText.trim()) { setError('Please provide text or upload a file'); return; }
    if (!options.deckName.trim() && !addMore) { setError('Please provide a deck name'); return; }

    setError(null);

    if (addMore) setIsAddingMore(true);
    else { setIsGenerating(true); setStep('generating'); }

    const provider = configuredProviders.find(p => p.id === options.provider);
    const effectiveProvider = provider || configuredProviders.find(p => p.id === 'pollination');

    if (!effectiveProvider) {
      setError('No AI provider available. Please configure one in Admin settings.');
      setStep('options');
      setIsGenerating(false);
      setIsAddingMore(false);
      return;
    }

    try {
      const result = await generateFlashcards(
        sourceText,
        { ...options, provider: options.provider, model: options.model },
        effectiveProvider
      );

      if (addMore) {
        setGeneratedCards(prev => {
          const combined = [...prev, ...result.cards];
          const seen = new Set<string>();
          return combined.filter(c => {
            const key = `${c.front}|||${c.back}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        });
      } else {
        setGeneratedCards(result.cards);
      }
      setGenerationMeta(result.metadata);
      setStep('review');
    } catch (err) {
      const errorMsg = (err as Error).message;
      setError(errorMsg);

      const otherProviders = availableProviders.filter(p => p.id !== options.provider);
      if (otherProviders.length > 0) {
        const nextProvider = otherProviders[0];
        setOptions(prev => ({ ...prev, provider: nextProvider.id, model: nextProvider.selectedModel }));
        setError(`${errorMsg}. Switched to ${nextProvider.name}. Try again or choose another provider.`);
      }

      setStep('options');
    } finally {
      setIsGenerating(false);
      setIsAddingMore(false);
      setProgressMessage('');
    }
  };

  const handleRetryWithFallback = async () => {
    const otherProviders = availableProviders.filter(p => p.id !== options.provider);
    if (otherProviders.length > 0) {
      const nextProvider = otherProviders[0];
      setOptions(prev => ({ ...prev, provider: nextProvider.id, model: nextProvider.selectedModel }));
      setSelectedProviderId(nextProvider.id);
      setSelectedModel(nextProvider.selectedModel);
      await handleGenerate(false);
    }
  };

  const handleSave = () => {
    if (generatedCards.length === 0) return;
    addDeck({
      name: options.deckName,
      description: `Generated from ${uploadedFiles.map(f => f.name).join(', ') || 'text input'}`,
      cards: generatedCards,
      tags: options.tags,
      language: options.language,
      isFavorite: false,
      color: 'blue',
      source: 'ai',
    });
    navigate('/decks');
  };

  const handleRegenerate = () => {
    setGeneratedCards([]);
    setGenerationMeta(null);
    setError(null);
    setStep('options');
  };

  const fallbackProviders = availableProviders.filter(p => p.id !== options.provider);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        {step !== 'upload' && (
          <button onClick={() => setStep('upload')} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        )}
        <h1 className="text-2xl font-bold">Generate Flashcards</h1>
      </div>

      {step === 'upload' && (
        <div className="space-y-8">
          <FileUploader onFilesProcessed={handleFilesProcessed} />
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
            <div className="relative flex justify-center text-sm"><span className="px-2 bg-gray-50 text-gray-500">or</span></div>
          </div>
          <div className="space-y-3">
            <label className="block text-sm font-medium">Paste Text Content</label>
            <TextArea value={textInput} onChange={e => setTextInput(e.target.value)} placeholder="Paste study material..." rows={6} />
          </div>
          <div className="flex justify-end">
            <Button onClick={() => setStep('options')} disabled={!sourceText.trim()} rightIcon={<ArrowRight className="w-4 h-4" />}>Continue</Button>
          </div>
        </div>
      )}

      {step === 'options' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border p-6 space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2"><Settings className="w-5 h-5" /> Generation Options</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Deck Name" value={options.deckName} onChange={e => setOptions({ ...options, deckName: e.target.value })} placeholder="Deck name" />
              <Input label="Tags (comma-separated)" value={options.tags.join(', ')} onChange={e => setOptions({ ...options, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })} placeholder="e.g. biology" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select label="AI Provider" value={options.provider} onChange={e => handleProviderChange(e.target.value)}
                options={providerOptions.length > 0 ? providerOptions : [{ value: 'pollination', label: 'Pollination AI (Free)' }]} />
              {getModelOptions().length > 1 ? (
                <Select label="Model" value={selectedModel || options.model} onChange={e => handleModelChange(e.target.value)} options={getModelOptions()} />
              ) : (
                <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <span className="text-sm">Model: <strong>{options.model}</strong></span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select label="Cards" value={String(options.cardCount)} onChange={e => setOptions({ ...options, cardCount: Number(e.target.value) })}
                options={[{ value: '5', label: '5 cards' }, { value: '10', label: '10 cards' }, { value: '15', label: '15 cards' }, { value: '20', label: '20 cards' }, { value: '30', label: '30 cards' }, { value: '50', label: '50 cards' }]} />
              <Select label="Difficulty" value={options.difficulty} onChange={e => setOptions({ ...options, difficulty: e.target.value as GenerationOptions['difficulty'] })}
                options={[{ value: 'easy', label: 'Easy' }, { value: 'medium', label: 'Medium' }, { value: 'hard', label: 'Hard' }, { value: 'mixed', label: 'Mixed' }]} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select label="Format" value={options.outputFormat} onChange={e => setOptions({ ...options, outputFormat: e.target.value as GenerationOptions['outputFormat'] })}
                options={[{ value: 'basic', label: 'Basic Q&A' }, { value: 'cloze', label: 'Cloze Deletion' }, { value: 'reversible', label: 'Reversible' }]} />
              <Select label="Language" value={options.language} onChange={e => setOptions({ ...options, language: e.target.value })}
                options={[{ value: 'en', label: 'English' }, { value: 'es', label: 'Spanish' }, { value: 'fr', label: 'French' }, { value: 'de', label: 'German' }]} />
            </div>

            <Switch checked={options.includeHints} onChange={() => setOptions({ ...options, includeHints: !options.includeHints })} label="Include hints" />

            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center gap-2 text-green-700">
                <Zap className="w-4 h-4" />
                <span className="text-sm">Using: <strong>{availableProviders.find(p => p.id === options.provider)?.name || 'Pollination AI'}</strong> · {options.model}</span>
              </div>
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-700">{error}</p>
                  {fallbackProviders.length > 0 && (
                    <Button size="sm" variant="outline" onClick={handleRetryWithFallback} className="mt-2" leftIcon={<RefreshCw className="w-4 h-4" />}>
                      Try {fallbackProviders[0].name} Instead
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={() => handleGenerate(false)} loading={isGenerating} leftIcon={<Wand2 className="w-5 h-5" />}>
              Generate {options.cardCount} Cards
            </Button>
          </div>
        </div>
      )}

      {step === 'generating' && (
        <div className="text-center py-16 space-y-4">
          <Loader2 className="w-12 h-12 text-blue-600 mx-auto animate-spin" />
          <h2 className="text-xl font-semibold">Generating flashcards...</h2>
          <p className="text-gray-500">{availableProviders.find(p => p.id === options.provider)?.name || 'Pollination AI'} is creating your cards</p>
        </div>
      )}

      {step === 'review' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-yellow-500" /> Generated Flashcards</h2>
              <p className="text-gray-500 text-sm">{generatedCards.length} cards {generationMeta && <span className="ml-2">· {generationMeta.provider} · {generationMeta.model}</span>}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={String(additionalCount)} onChange={e => setAdditionalCount(Number(e.target.value))} options={[{ value: '5', label: '5 more' }, { value: '10', label: '10 more' }, { value: '15', label: '15 more' }]} className="w-28" />
              <Button variant="outline" size="sm" onClick={() => handleGenerate(true)} loading={isAddingMore} leftIcon={<Plus className="w-4 h-4" />}>Add More</Button>
              <Button variant="ghost" size="sm" onClick={handleRegenerate}>Regenerate</Button>
              <Button onClick={handleSave} leftIcon={<CheckCircle className="w-4 h-4" />}>Save ({generatedCards.length})</Button>
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 inline mr-2" />{error}
            </div>
          )}

          {generatedCards.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {generatedCards.map(card => <FlashcardCard key={card.id} card={card} showActions={false} />)}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">No cards generated.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default GeneratePage;